import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import authentication components
import ProtectedRoute from './components/auth/ProtectedRoute';

// Import contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AuthModal from './components/auth/AuthModal';

// Import API configuration
import { API_BASE_URL } from './config/api';

// Import i18n
import './i18n';

// Lazy load components for better performance
const Navbar = lazy(() => import('./components/Navbar'));
const HeroSection = lazy(() => import('./components/HeroSection'));
const ServicesSection = lazy(() => import('./components/ServicesSection'));
const OurStrengthsSection = lazy(() => import('./components/OurStrengthsSection'));
const DoctorsSection = lazy(() => import('./components/DoctorsSection'));
const AboutSection = lazy(() => import('./components/AboutSection'));
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const CoreValuesSection = lazy(() => import('./components/CoreValuesSection'));
const WhyChooseUsSection = lazy(() => import('./components/WhyChooseUsSection'));
const FAQSection = lazy(() => import('./components/FAQSection'));
const TeamSection = lazy(() => import('./components/TeamSection'));
const LocationSection = lazy(() => import('./components/LocationSection'));
const BlogSection = lazy(() => import('./components/BlogSection'));
const BlogPage = lazy(() => import('./components/BlogPage'));
const GallerySection = lazy(() => import('./components/GallerySection'));
const Footer = lazy(() => import('./components/Footer'));
const FloatingSvgElements = lazy(() => import('./components/FloatingSvgElements'));
const WhatsAppButton = lazy(() => import('./components/WhatsAppButton'));
const VideoHeroSection = lazy(() => import('./components/VideoHeroSection'));
const PatientMomentsSection = lazy(() => import('./components/PatientMomentsSection'));
// Lazy load dashboard components
const PatientDashboard = lazy(() => import('./components/dashboards/PatientDashboard'));
const DoctorDashboard = lazy(() => import('./components/dashboards/DoctorDashboard'));
const AdminDashboard = lazy(() => import('./components/dashboards/AdminDashboard'));
const ReceptionDashboard = lazy(() => import('./components/ReceptionDashboard'));

// Set axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-tiffany mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-300 font-montserrat">Loading Lumen Smile...</p>
    </div>
  </div>
);

// Landing Page Component
const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [websiteContent, setWebsiteContent] = useState(null);
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [coreValues, setCoreValues] = useState([]);
  const [whyChooseUsItems, setWhyChooseUsItems] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [ourStrengths, setOurStrengths] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 200]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching data from API...');
        console.log('API Base URL:', axios.defaults.baseURL);
        
        // Get current language from i18n
        const currentLang = i18n.language || 'en';
        const langParam = `?lang=${currentLang}`;
        
        const [contentRes, servicesRes, doctorsRes, testimonialsRes, coreValuesRes, whyChooseUsRes, faqsRes, teamRes, ourStrengthsRes, blogRes, galleryItemsRes, galleryCategoriesRes] = await Promise.all([
          axios.get(`website-content/${langParam}`),
          axios.get(`services/${langParam}`),
          axios.get(`doctors/${langParam}`),
          axios.get(`testimonials/${langParam}`),
          axios.get(`core-values/${langParam}`),
          axios.get(`why-choose-us/${langParam}`),
          axios.get(`faq/${langParam}`),
          axios.get(`team/${langParam}`),
          axios.get(`our-strengths/${langParam}`),
          axios.get(`blog/${langParam}`),
          axios.get(`gallery/items/${langParam}`),
          axios.get(`gallery/categories/${langParam}`),
        ]);

        console.log('API Responses:', {
          content: contentRes.data,
          services: servicesRes.data,
          doctors: doctorsRes.data,
          testimonials: testimonialsRes.data,
          coreValues: coreValuesRes.data,
          whyChooseUs: whyChooseUsRes.data,
          faqs: faqsRes.data,
          team: teamRes.data,
          ourStrengths: ourStrengthsRes.data,
          blog: blogRes.data,
          galleryItems: galleryItemsRes.data,
          galleryCategories: galleryCategoriesRes.data
        });

        console.log('Services count:', servicesRes.data?.length || 0);
        console.log('Doctors count:', doctorsRes.data?.length || 0);

        setWebsiteContent(contentRes.data[0] || {});
        setServices(servicesRes.data || []);
        setDoctors(doctorsRes.data || []);
        setTestimonials(testimonialsRes.data || []);
        setCoreValues(coreValuesRes.data || []);
        setWhyChooseUsItems(whyChooseUsRes.data || []);
        setFaqs(faqsRes.data || []);
        setTeamMembers(teamRes.data || []);
        setOurStrengths(ourStrengthsRes.data || []);
        setBlogPosts(blogRes.data || []);
        setGalleryItems(galleryItemsRes.data || []);
        setGalleryCategories(galleryCategoriesRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        console.error('Error details:', error.response?.data);
        console.error('Error status:', error.response?.status);
        console.error('Error URL:', error.config?.url);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [i18n.language]);

  // Scroll to section if coming from navbar
  useEffect(() => {
    if (!loading && location.state && location.state.scrollTo) {
      let attempts = 0;
      const timer = setInterval(() => {
        const el = document.getElementById(location.state.scrollTo);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          clearInterval(timer);
          navigate('.', { replace: true, state: {} });
        }
        if (++attempts >= 100) clearInterval(timer);
      }, 100);
      return () => clearInterval(timer);
    }
  }, [location.state, navigate, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-tiffany text-2xl font-playfair animate-pulse">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="App bg-white dark:bg-gray-900 text-black dark:text-white overflow-x-hidden">
      {/* Floating SVG Background Elements */}
      <Suspense fallback={<LoadingSpinner />}>
        <FloatingSvgElements />
      </Suspense>

      {/* Main Content */}
      <div className="relative z-10">
        <Suspense fallback={<LoadingSpinner />}>
          <Navbar />
        </Suspense>
        
        <main>
          <Suspense fallback={<LoadingSpinner />}>
            <VideoHeroSection />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <HeroSection content={websiteContent} />
          </Suspense>
       <Suspense fallback={<LoadingSpinner />}>
  <PatientMomentsSection />
</Suspense>

<Suspense fallback={<LoadingSpinner />}>
  <ServicesSection services={services} content={websiteContent} />
</Suspense>

<Suspense fallback={<LoadingSpinner />}>
  <OurStrengthsSection
    ourStrengths={ourStrengths}
    content={websiteContent}
  />
</Suspense>

<Suspense fallback={<LoadingSpinner />}>
  <GallerySection
    galleryItems={galleryItems}
    galleryCategories={galleryCategories}
    content={websiteContent}
  />
</Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <DoctorsSection doctors={doctors} content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <CoreValuesSection coreValues={coreValues} content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <WhyChooseUsSection whyChooseUsItems={whyChooseUsItems} content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <AboutSection content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <TeamSection teamMembers={teamMembers} content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <TestimonialsSection testimonials={testimonials} content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <BlogSection blogPosts={blogPosts} content={websiteContent} />
          </Suspense>
         
          <Suspense fallback={<LoadingSpinner />}>
            <LocationSection content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <FAQSection faqs={faqs} content={websiteContent} />
          </Suspense>
          <Suspense fallback={<LoadingSpinner />}>
            <ContactSection content={websiteContent} />
          </Suspense>
        </main>
        
        <Suspense fallback={<LoadingSpinner />}>
          <Footer content={websiteContent} />
        </Suspense>
      </div>

      {/* WhatsApp Floating Button */}
      <Suspense fallback={<LoadingSpinner />}>
        <WhatsAppButton content={websiteContent} />
      </Suspense>

      {/* Parallax Background Elements */}
      <motion.div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{ y }}
      >
        <div className="absolute top-20 left-10 w-20 h-20 bg-tiffany/20 rounded-full blur-xl animate-float-slow" />
        <div className="absolute bottom-40 left-1/4 w-16 h-16 bg-tiffany/30 rounded-full blur-xl animate-float-fast" />
      </motion.div>
    </div>
  );
};

// Main App Component with Routing
const AppContent = () => {
  const { isAuthenticated, getUserType } = useAuth();

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');

  useEffect(() => {
    const handler = (event) => {
      setAuthTab(event.detail?.tab || 'login');
      setAuthModalOpen(true);
    };

    window.addEventListener('openAuthModal', handler);

    return () => {
      window.removeEventListener('openAuthModal', handler);
    };
  }, []);

  return (
    <>
      <Router>
        <Routes>
  {/* Public Routes */}
  <Route path="/" element={<LandingPage />} />
  <Route path="/blog" element={<Suspense fallback={<LoadingSpinner />}><BlogPage /></Suspense>} />
  <Route path="/blog/:slug" element={<Suspense fallback={<LoadingSpinner />}><BlogPage /></Suspense>} />

  {/* Protected Routes */}
  <Route 
    path="/patient/dashboard" 
    element={
      <ProtectedRoute allowedRoles={['patient']}>
        <Suspense fallback={<LoadingSpinner />}>
          <PatientDashboard />
        </Suspense>
      </ProtectedRoute>
    }
  />

  <Route 
    path="/doctor/dashboard" 
    element={
      <ProtectedRoute allowedRoles={['doctor']}>
        <Suspense fallback={<LoadingSpinner />}>
          <DoctorDashboard />
        </Suspense>
      </ProtectedRoute>
    }
  />

  <Route 
    path="/admin/dashboard" 
    element={
      <ProtectedRoute allowedRoles={['admin']}>
        <Suspense fallback={<LoadingSpinner />}>
          <AdminDashboard />
        </Suspense>
      </ProtectedRoute>
    }
  />
    <Route
  path="/reception/dashboard"
  element={
    <ProtectedRoute allowedRoles={['staff']}>
      <Suspense fallback={<LoadingSpinner />}>
        <ReceptionDashboard />
      </Suspense>
    </ProtectedRoute>
  }
/>

  {/* Dashboard redirect */}
  <Route
    path="/dashboard"
    element={
      isAuthenticated() ? (
        (() => {
          const userType = getUserType();
         switch (userType) {
  case 'admin':
    return <Navigate to="/admin/dashboard" replace />;

  case 'doctor':
    return <Navigate to="/doctor/dashboard" replace />;

  case 'staff':
    return <Navigate to="/reception/dashboard" replace />;

  default:
    return <Navigate to="/patient/dashboard" replace />;
}
        })()
      ) : (
        <Navigate to="/" replace />
      )
    }
  />

  {/* Catch-all */}
  <Route path="*" element={<Navigate to="/" replace />} />
</Routes>

      </Router>

      {/* 🔥 THIS IS WHAT WAS MISSING */}
      <AuthModal
  isOpen={authModalOpen}
  activeTab={authTab}
  onClose={() => setAuthModalOpen(false)}
/>

    </>
  );
};


// Root App Component with Auth Provider
const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
